import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { describePage } from "./drift-alerts";

interface DriftAlertsPaginationProps {
  readonly totalCount: number;
  readonly limit: number;
  readonly offset: number;
  readonly onOffsetChange: (offset: number) => void;
}

export function DriftAlertsPagination({
  totalCount,
  limit,
  offset,
  onOffsetChange,
}: DriftAlertsPaginationProps) {
  const { t } = useTranslation();
  const { page, totalPages, from, to, hasPrev, hasNext } = describePage({
    totalCount,
    limit,
    offset,
  });

  return (
    <div data-testid="drift-alerts-pagination" className="flex items-center justify-between gap-3">
      <p
        data-testid="drift-alerts-pagination-summary"
        className="font-sans text-sm font-normal text-muted-foreground"
      >
        {t("subscriptions.driftAlerts.pagination.summary", {
          from,
          to,
          total: totalCount,
        })}
      </p>
      <div className="flex items-center gap-2">
        <Button
          data-testid="drift-alerts-pagination-prev"
          variant="outline"
          size="sm"
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
          disabled={!hasPrev}
          aria-label={t("subscriptions.driftAlerts.pagination.previous")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-sans text-sm font-medium tabular-nums text-foreground">
          {t("subscriptions.driftAlerts.pagination.pageOf", {
            page,
            total: totalPages,
          })}
        </span>
        <Button
          data-testid="drift-alerts-pagination-next"
          variant="outline"
          size="sm"
          onClick={() => onOffsetChange(offset + limit)}
          disabled={!hasNext}
          aria-label={t("subscriptions.driftAlerts.pagination.next")}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
